import { Component, OnInit, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CapitalContainer } from '@commons/capital-container/capital-container';
import { CapitalBack } from '@commons/capital-back/capital-back';
import { CapitalCard } from '@commons/capital-card/capital-card';
import { CapitalSelect } from '@commons/capital-select/capital-select';
import { PeriodSelector, PeriodOption } from '@commons/period-selector/period-selector';
import { PrimaryButton } from '@commons/primary-button/primary-button';
import { AuthService } from '@commons/services/auth.service';
import { CertificateConfigService, CertificateConfig, CertificateField } from '@commons/services/certificate-config.service';
import { CertificadosService } from '@commons/services/certificados.service';

@Component({
  selector: 'app-generic-certificate',
  imports: [
    CommonModule,
    FormsModule,
    CapitalContainer,
    CapitalBack,
    CapitalCard,
    CapitalSelect,
    PeriodSelector,
    PrimaryButton
  ],
  templateUrl: './generic-certificate.html',
  styleUrl: './generic-certificate.scss',
})
export class GenericCertificate implements OnInit {
  certificateConfig = signal<CertificateConfig | null>(null);
  certificateIndex = signal<number>(0);
  
  // Valores de los campos del formulario
  formValues: { [key: string]: any } = {};
  
  // Email del usuario
  userEmail = signal<string>('');
  
  // Estado de carga
  isLoading = signal<boolean>(false);
  isLoadingConfig = signal<boolean>(true);
  errorMessage = signal<string | null>(null);

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly configService: CertificateConfigService,
    private readonly certificadosService: CertificadosService
  ) {
    // Obtener email del usuario
    const datos = this.authService.datosPersonales();
    if (datos?.email) {
      this.userEmail.set(this.maskEmail(datos.email));
    }
  }

  ngOnInit(): void {
    // Obtener el índice del certificado desde la ruta
    this.route.params.subscribe(async params => {
      const index = Number.parseInt(params['certificateId'], 10);
      
      if (Number.isNaN(index)) {
        this.errorMessage.set('Índice de certificado inválido');
        this.router.navigate(['/certificates']);
        return;
      }
      
      this.certificateIndex.set(index);
      
      try {
        // Cargar configuración desde el BFF
        const config = await this.configService.getConfig(index);
        this.certificateConfig.set(config);
        
        // Inicializar valores por defecto de los campos usando fieldName
        config.fields?.forEach(field => {
          if (field.defaultValue !== undefined) {
            // Usar fieldName como clave del objeto formValues
            this.formValues[field.fieldName] = String(field.defaultValue);
          }
        });
      } catch (error) {
        console.error('Error cargando configuración del certificado:', error);
        this.errorMessage.set('No se pudo cargar la configuración del certificado');
        // Redirigir después de 2 segundos
        setTimeout(() => this.router.navigate(['/certificates']), 2000);
      } finally {
        this.isLoadingConfig.set(false);
      }
    });
  }

  onFieldChange(fieldName: string, value: any) {
    this.formValues[fieldName] = value;
  }

  getPeriodOptions(field: CertificateField): PeriodOption[] {
    if (!field.options) return [];
    
    const options = field.options.map(opt => ({
      value: opt.value as number,
      label: opt.label
    }));
    
    // Si includeAllOption está activo, agregar opción "Todos"
    if (field.includeAllOption) {
      options.push({ value: -1, label: 'Todos' });
    }
    
    return options;
  }

  getDropdownOptions(field: CertificateField): string[] {
    if (!field.options) return [];
    return field.options.map(opt => opt.label);
  }

  getDropdownValues(field: CertificateField): string[] {
    if (!field.options) return [];
    return field.options.map(opt => String(opt.value));
  }

  getInitialPeriodValue(field: CertificateField): number | null {
    if (field.defaultValue === undefined) return null;
    if (typeof field.defaultValue === 'number') return field.defaultValue;
    // Si es string, intentar parsear
    const parsed = Number.parseInt(String(field.defaultValue), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  async enviarPorCorreo() {
    const config = this.certificateConfig();
    if (!config) return;
    
    this.isLoading.set(true);
    this.errorMessage.set(null);
    
    try {
      // Construir el request body usando los fieldName
      const requestData = this.buildRequestBody();
      
      // Enviar certificado por email
      const mensaje = await this.certificadosService.sendCertificateByEmail(
        this.certificateIndex(),
        requestData
      );
      
      // Mostrar mensaje de éxito
      alert(mensaje || 'Certificado enviado por correo exitosamente');
    } catch (error) {
      console.error('Error al enviar certificado por correo:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al enviar certificado';
      this.errorMessage.set(errorMsg);
      alert(`Error: ${errorMsg}`);
    } finally {
      this.isLoading.set(false);
    }
  }

  async imprimir() {
    const config = this.certificateConfig();
    if (!config) return;
    
    this.isLoading.set(true);
    this.errorMessage.set(null);
    
    try {
      // Construir el request body usando los fieldName
      const requestData = this.buildRequestBody();
      
      // Descargar certificado
      const pdfBase64 = await this.certificadosService.downloadCertificate(
        this.certificateIndex(),
        requestData
      );
      
      // Convertir Base64 a Blob y descargarlo
      this.downloadPDF(pdfBase64, `certificado-${config.category}.pdf`);
    } catch (error) {
      console.error('Error al descargar certificado:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al descargar certificado';
      this.errorMessage.set(errorMsg);
      alert(`Error: ${errorMsg}`);
    } finally {
      this.isLoading.set(false);
    }
  }

  volver() {
    this.router.navigate(['/certificates']);
  }

  /**
   * Construye el request body usando los fieldName de los campos
   */
  private buildRequestBody(): Record<string, any> {
    const config = this.certificateConfig();
    if (!config?.fields) return {};

    const requestData: Record<string, any> = {};

    config.fields.forEach(field => {
      const value = this.formValues[field.fieldName];
      
      if (value !== undefined && value !== null) {
        // Convertir el valor al tipo apropiado
        if (field.fieldName === 'numeroMeses') {
          // numeroMeses debe ser number
          requestData[field.fieldName] = Number(value);
        } else {
          // tipoConsulta y tipoProducto son strings
          requestData[field.fieldName] = String(value);
        }
      } else if (field.defaultValue !== undefined) {
        // Usar valor por defecto si no hay valor en formValues
        if (field.fieldName === 'numeroMeses') {
          requestData[field.fieldName] = Number(field.defaultValue);
        } else {
          requestData[field.fieldName] = String(field.defaultValue);
        }
      }
    });

    return requestData;
  }

  /**
   * Descarga un archivo PDF desde Base64
   */
  private downloadPDF(base64: string, filename: string): void {
    try {
      // Convertir Base64 a Blob
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Crear URL y descargar
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      
      // Limpiar
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al procesar PDF:', error);
      throw new Error('Error al procesar el archivo PDF');
    }
  }

  /**
   * Enmascara un email mostrando solo algunos caracteres
   */
  private maskEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;

    const visibleStart = localPart.substring(0, Math.min(4, localPart.length));
    const domainParts = domain.split('.');
    const visibleEnd = domainParts.at(-1) || '';
    const maskedLength = email.length - visibleStart.length - visibleEnd.length;
    const masked = '*'.repeat(maskedLength);

    return `${visibleStart}${masked}${visibleEnd}`;
  }
}
